import React from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { formatCount } from "../../../utils/numberFormat";

// DonutChart Component
const DonutChart = ({ data, centerValue, centerLabel }) => {
  const allStatusData = [
    { name: "Toilet Count", value: 0, color: "#10b981" },
    { name: "Water Source", value: 0, color: "#3b82f6" },
    { name: "Hygiene Program", value: 0, color: "#f59e0b" },
    { name: "Flood Affected", value: 0, color: "#ef4444" },
  ];

  if (data && data.length > 0) {
    data.forEach((item) => {
      const existingIndex = allStatusData.findIndex(
        (status) => status.name === item.name
      );
      if (existingIndex !== -1) {
        allStatusData[existingIndex].value = item.value;
      }
    });
  }

  const total = allStatusData.reduce((sum, item) => sum + item.value, 0);
  const radius = 40;
  const strokeWidth = 8;
  const center = radius + strokeWidth;
  const circumference = 2 * Math.PI * radius;
  let cumulativePercentage = 0;

  const leftSideData = allStatusData.filter(
    (item) => item.name === "Toilet Count" || item.name === "Water Source"
  );
  const rightSideData = allStatusData.filter(
    (item) => item.name === "Hygiene Program" || item.name === "Flood Affected"
  );


  return (
    <div className="flex items-center justify-center space-x-3 bg-white">
      {/* Left Legend */}
      <div className="flex flex-col space-y-2">
        {leftSideData.map((item, index) => (
          <div
            key={index}
            className="flex justify-center items-center space-x-3 text-xs"
          >
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: item.color }}
            ></div>
            <div className="flex flex-col min-w-[70px]">
              <span className="font-bold text-gray-800">
                {formatCount(item.value)}
              </span>
              <span className="text-gray-500 text-[10px] truncate">
                {item.name}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Donut Chart */}
      <div
        className="relative flex-shrink-0"
        style={{
          width: center * 2,
          height: center * 2,
          boxShadow: "none", // <-- ensure no shadow
          border: "none", // <-- ensure no dividing border
        }}
      >
        <svg
          width={center * 2}
          height={center * 2}
          className="transform -rotate-90"
        >
          {/* Background Circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke="#f3f4f6"
            strokeWidth={strokeWidth}
            fill="transparent"
          />

          {/* Chart Segments */}
          {allStatusData
            .filter((item) => item.value > 0)
            .map((item, index) => {
              const percentage = total > 0 ? (item.value / total) * 100 : 0;
              const strokeDasharray = `${
                (percentage / 100) * circumference
              } ${circumference}`;
              const strokeDashoffset = -(
                (cumulativePercentage / 100) * circumference
              );

              const segment = (
                <circle
                  key={index}
                  cx={center}
                  cy={center}
                  r={radius}
                  stroke={item.color}
                  strokeWidth={strokeWidth}
                  fill="transparent"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  className="transition-all duration-300"
                />
              );

              cumulativePercentage += percentage;
              return segment;
            })}
        </svg>

        {/* Center Text */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-lg font-bold text-gray-800">{formatCount(centerValue)}</div>
          <div className="text-xs text-gray-600">{centerLabel}</div>
        </div>
      </div>

      {/* Right Legend */}
      <div className="flex flex-col space-y-2">
        {rightSideData.map((item, index) => (
          <div
            key={index}
            className="flex justify-center items-center space-x-3 text-xs"
          >
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ backgroundColor: item.color }}
            ></div>
            <div className="flex flex-col min-w-[70px]">
              <span className="font-bold text-gray-800">
                {formatCount(item.value)}
              </span>
              <span className="text-gray-500 text-[10px] truncate">
                {item.name}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main WashCards
const WashCards = () => {
  const toiletCardFilter = useSelector(
    (state) => state.dashboard?.toiletCardCount ?? 0
  );
  const waterCardFilter = useSelector(
    (state) => state.dashboard?.waterCardCount ?? 0
  );
  const hygeineCardFilter = useSelector(
    (state) => state.dashboard?.hygeineCardCount ?? 0
  );
  const floodCardFilter = useSelector(
    (state) => state.dashboard?.floodCardCount ?? 0
  );

  const totalCount =
    toiletCardFilter + waterCardFilter + hygeineCardFilter + floodCardFilter;

  return (
    <Box sx={{ width: "100%", boxShadow: "none" }}>
      <div className="text-center mt-5 bg-white shadow-none border-none">
        <h3 className="text-center text-xl my-2 p-0 text-[#3A3A3A] font-bold uppercase text-balance">
          Status
        </h3>
      </div>
      <DonutChart
        data={[
          { name: "Toilet Count", value: toiletCardFilter, color: "#10b981" },
          { name: "Water Source", value: waterCardFilter, color: "#3b82f6" },
          { name: "Hygiene Program", value: hygeineCardFilter, color: "#f59e0b" },
          { name: "Flood Affected", value: floodCardFilter, color: "#ef4444" },
        ]}
        centerValue={totalCount}
        centerLabel="Total"
      />
    </Box>
  );
};

export default WashCards;
